//Props Validations
import PropTypes from "prop-types";
import ReactECharts from "echarts-for-react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector } from "react-redux";
import VariableCategoryColorMap from "utils/VariableCategoryColorMap";

function DriverAnalysisHorizontalBarChart({ variableKpi = [], algorithm = "" }) {
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = isHairCare || isSkinCare;
    const isRelativeWeightAnalysis = (algorithm || "").toLowerCase() === "relative_weight_analysis";

    const sortedVariableKpi = [...variableKpi].sort((a, b) => Object.values(a)[0] - Object.values(b)[0]);

    const keys = sortedVariableKpi.map((item) => Object.keys(item)[0]);
    const values = sortedVariableKpi.map((item) => Object.values(item)[0]);

    const customRich = {};
    Object.keys(VariableCategoryColorMap).forEach((ele) => {
        customRich[ele.replace(/[()]/g, "").replace(/\s+/g, "").toLowerCase()] = {
            color: VariableCategoryColorMap[ele],
        };
    });

    const fontSize = keys.length <= 20 ? 13 : 11;

    const getBarColor = (score, dataIndex) => {
        const defaultBlue = "#0060ee";


        // Apply yellow palette for Partial Least Squares AND only for Hair/Skin categories
        const isPartialLeastSquares = (algorithm || "").toLowerCase() === "partial_least_squares";
        const rank = dataIndex / values.length;

        if (isPartialLeastSquares && isBeautyAndWellbeing) {
            if (score > 1) {
                return "#FFD700"; // Yellow
            } else if (score < 1) {
                return "#FFFACD"; // Light Yellow
            }
        } else if (isRelativeWeightAnalysis && isBeautyAndWellbeing) {
            if (rank >= 0.75) return "#D21717";
            if (rank >= 0.5) return "#F59D27";
            if (rank >= 0.25) return "#FFDA00";
            return "#01A801";
        }

        return defaultBlue;
    };

    const options = {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            formatter: function (params) {
                const label = params[0]?.axisValueLabel;
                const [first, second] = label?.split("*-*") || [];
                const color = VariableCategoryColorMap[second] || "black";

                let tooltipText = `<span style=font-size:12px;">${first || label}&nbsp;</span><span style="color: ${color}; font-size:12px;">${second || ""}</span><br/>`;
                const score = params[0].data;
                tooltipText += `${params[0].marker} <span style="float:right; font-weight:bold; padding-left:20px; font-size:12px;">${score}</span><br/>`;

                return tooltipText;
            },
        },
        grid: {
            left: "4%",
            right: "4%",
            bottom: "4%",
            top: "4%",
            containLabel: true,
        },
        xAxis: {
            type: "value",
            boundaryGap: [0, 0.05],
            axisLabel: {
                textStyle: {
                    color: "black",
                    fontSize: 13,
                },
            },
        },
        yAxis: {
            type: "category",
            data: keys,
            axisLabel: {
                interval: 0,
                rich: {
                    ...customRich,
                    default: {
                        color: "black", // Default color for other parts
                    },
                },
                formatter: function (label) {
                    const [_option, kpiData] = label.split("*-*");
                    const colorKey = kpiData?.replace(/[()]/g, "").replace(/\s+/g, "").toLowerCase();
                    return `${_option} {${colorKey}|${kpiData}}`;
                },
                textStyle: {
                    fontSize: fontSize,
                },
            },
        },
        series: [
            {
                type: "bar",
                data: values,
                itemStyle: {
                    // Color based on score
                    color: function (params) {
                        const score = params.data;
                        return getBarColor(score, params.dataIndex);
                    },
                },
                label: {
                    show: true,
                    position: "right",
                    formatter: "{c}",
                    textStyle: {
                        color: "black",
                        fontSize: fontSize - 1,
                    },
                },
            },
        ],
    };

    const RWA_LEGEND = [
        { color: "#D21717", label: "Top 25%" },
        { color: "#F59D27", label: "50%\u201375%" },
        { color: "#FFDA00", label: "25%\u201350%" },
        { color: "#01A801", label: "Bottom 25%" },
    ];

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <ReactECharts
                option={options}
                style={{ height: keys.length > 20 ? 900 : 450, width: "100%" }}
            />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "22px",
                    fontSize: "18px",
                    color: "black",
                }}
            >
                <span style={{ marginRight: "6px" }}>Importance Level</span>
                <ArrowForwardIcon />
            </div>
            {isRelativeWeightAnalysis && isBeautyAndWellbeing && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "24px",
                        marginTop: "22px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                    }}
                >
                    {RWA_LEGEND.map(({ color, label }) => (
                        <div
                            key={label}
                            style={{ display: "flex", alignItems: "center", gap: "6px" }}
                        >
                            <span
                                style={{
                                    display: "inline-block",
                                    width: 16,
                                    height: 16,
                                    borderRadius: "3px",
                                    backgroundColor: color,
                                    flexShrink: 0,
                                }}
                            />
                            <span style={{ fontSize: "18px", color: "black" }}>{label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Typechecking props for the DriverAnalysis
DriverAnalysisHorizontalBarChart.propTypes = {
    variableKpi: PropTypes.array,
    algorithm: PropTypes.string,
};

export default DriverAnalysisHorizontalBarChart;
